import React from 'react';
import { Typography, Button, Space } from 'antd';
import SafeMarkdown from 'components/SafeMarkdown';
import useAntdToken from 'hooks/useAntdToken';
import { StatusModalButton } from './StatusModal';

const { Title, Text } = Typography;

export interface ContentBadge {
  text: string;
  backgroundColor: string;
  color: string;
}

interface ContentModalProps {
  // Badge configuration
  badge?: ContentBadge;

  // Content
  title: string;
  content: string;
  isMarkdown?: boolean;

  // Action buttons
  buttons?: StatusModalButton[];

  // Progress indicator
  progress?: {
    current: number;
    total: number;
    label?: string; // e.g., "公告", "通知"
  };

  // Styling
  maxHeight?: string;
  contentPadding?: string;
}

const ContentModal: React.FC<ContentModalProps> = ({
  badge,
  title,
  content,
  isMarkdown = true,
  buttons = [],
  progress,
  maxHeight = '60vh',
  contentPadding = '16px',
}) => {
  const { token } = useAntdToken();

  return (
    <div style={{ maxHeight, overflowY: 'auto' }}>
      {/* Content */}
      <div style={{ marginBottom: '24px' }}>
        <Title
          level={4}
          style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {badge && (
            <span
              style={{
                backgroundColor: badge.backgroundColor,
                color: badge.color,
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                lineHeight: '1',
              }}
            >
              {badge.text}
            </span>
          )}
          <span>{title}</span>
        </Title>

        <div
          style={{
            backgroundColor: '#fafafa',
            padding: contentPadding,
            borderRadius: '6px',
            border: `1px solid ${token.colorBorder}`,
          }}
        >
          {isMarkdown ? (
            <SafeMarkdown wrapper="article">{content}</SafeMarkdown>
          ) : (
            <div>{content}</div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {buttons.length > 0 && (
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Space>
            {buttons.map((button) => (
              <Button
                key={button.key}
                type={button.type}
                danger={button.danger}
                icon={button.icon}
                onClick={button.onClick}
                loading={button.loading}
                size={button.size || 'large'}
              >
                {button.children}
              </Button>
            ))}
          </Space>
        </div>
      )}

      {/* Progress indicator */}
      {progress && progress.total > 1 && (
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Text type="secondary">
            {progress.current} / {progress.total} 則數
          </Text>
        </div>
      )}
    </div>
  );
};

// Export preset badge configurations for convenience
export const CONTENT_BADGES = {
  consent: {
    text: '需要確認',
    backgroundColor: '#fff2f0',
    color: '#ff4d4f',
  },
  info: {
    text: '系統通知',
    backgroundColor: '#e6f7ff',
    color: '#1890ff',
  },
  warning: {
    text: '重要通知',
    backgroundColor: '#fffbe6',
    color: '#faad14',
  },
  success: {
    text: '完成',
    backgroundColor: '#f6ffed',
    color: '#52c41a',
  },
} as const;

export default ContentModal;
